import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  WorkDocument,
  WorkDocumentsV2Service,
  WorkDocumenttypeIdentifierNameEnum,
  WorkPermissionsEnum,
  WorkProject,
} from '@work-frontend-monorepo/work-rest-api';
import dayjs from 'dayjs';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '../icon/icon.component';
import { CheckboxComponent } from '../inputs/checkbox/checkbox.component';
import { LightboxComponent, ViewableGalleryDocument } from '../lightbox/lightbox.component';
import { ButtonComponent } from '../button/button.component';
import { StickyFooterComponent } from '../sticky-footer/sticky-footer.component';
import { lastValueFrom } from 'rxjs';
import { ExceptionHandlingService } from '../../services/exception-handling/exception-handling.service';
import { ConfirmActionModalDirective } from '../../directives/confirm-action-modal/confirm-action-modal.directive';
import { AlertDialogService } from '../../services/alert-dialog/alert-dialog.service';
import { environment } from '@work-frontend-monorepo/work-environments';
import { BadgeComponent } from '../badge/badge.component';
import { StringMap } from '../../models/string-map.type';
import { LoadingAndFeedbackState } from '../../models/loading-and-feedback-state.type';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { PrependServerToFileSrcPipe } from '../../pipes/prepend-server-to-file-src.pipe';
import { DropzoneMultiComponent } from '../dropzone-multi/dropzone-multi.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { ProfileState } from '../../state/states/profile/profile-state';

@Component({
  selector: 'work-ui-gallery',
  templateUrl: './gallery.component.html',
  styleUrl: './gallery.component.scss',
  standalone: true,
  imports: [
    PrependServerToFileSrcPipe,
    FormatDatePipe,
    TranslateModule,
    IconComponent,
    CheckboxComponent,
    LightboxComponent,
    ButtonComponent,
    StickyFooterComponent,
    ConfirmActionModalDirective,
    BadgeComponent,
    DropzoneMultiComponent,
  ],
})
export class GalleryComponent implements OnDestroy, OnInit {
  /**
   * Die Lightbox arbeitet mit dem schmaleren `ViewableGalleryDocument` (sie wird auch von der
   * externen Projektfreigabe genutzt). Hier ist die Eingabe aber garantiert `WorkDocument[]`, der
   * Cast an dieser einen Stelle ist deshalb korrekt.
   */
  protected onLightboxDocumentDeleted(document: ViewableGalleryDocument): void {
    this.documentDeleted.emit([document as WorkDocument]);
  }

  private readonly documentsV2Service = inject(WorkDocumentsV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly store = inject(Store);

  @ViewChild('lightbox') lightbox!: LightboxComponent;
  public galleryDocuments = input.required<WorkDocument[]>();
  public selectModeEnabled = input<boolean>(true);
  /**
   * Upload-Dropzone und Hochladen-Knopf ausblenden. Nötig für reine Anzeige-Kontexte wie das
   * Projektdossier: dort bündelt die Galerie Fotos mehrerer Dokumenttypen/Subtypen, es gibt also
   * kein eindeutiges Upload-Ziel.
   */
  public uploadEnabled = input<boolean>(true);
  /** Fotos in der Lightbox beschriften (siehe `LightboxComponent.annotationEnabled`). */
  public annotationEnabled = input<boolean>(false);
  public projectId = input<number | null>();
  /** Hochgeladene Fotos/Videos einem Akquiseobjekt zuordnen. */
  public prospectId = input<number | null>();
  public documentsubtypeId = input<number | null>();
  public documentTypeId = input<number | null>();
  public documentTypeIdentifierName = input<WorkDocumenttypeIdentifierNameEnum | null>();
  public folder = input<string | null>();
  public connectedProjects = input<WorkProject[]>([]);
  public documentsByDate = signal<{ date: string; selected: boolean; documents: WorkDocument[] }[]>([]);
  public projectNameMap: Signal<StringMap> = computed(() =>
    this.connectedProjects().reduce((acc: StringMap, project) => {
      acc[project.id] = project.numberQualified || '';
      return acc;
    }, {}),
  );
  public fileUploadLoadingState = signal<LoadingAndFeedbackState>('none');
  public showUploadOnDesktop: WritableSignal<boolean> = signal(false);
  public canDelete = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.DocumentDELETE)));
  // selection mode
  public selectedDocuments = model<WorkDocument[]>([]);
  public lastSelectedDocument: WorkDocument | undefined;
  public selectionModeActive = false;
  // selection mode batch select
  public batchSelectionActive = false;
  public batchSelectionDocuments: WorkDocument[] = [];
  public batchSelectionHoveredDocument: WorkDocument | undefined;
  public allDocumentsSelected = false;
  public documentDeleted = output<WorkDocument[]>();
  public newDocumentsUploaded = output<WorkDocument[]>();
  public uploadingStateChanged = output<boolean>();
  public moveSelectedDocuments = output();
  public documentAnnotated = output<WorkDocument>();
  private boundHandleKeyboardEvents = this.handleKeyboardEvents.bind(this);

  private galleryDocumentsEffect = effect(() => {
    this.orderDocumentsByDate();
  });
  private galleryDocumentsSelectedDocumentsEffect = effect(() => {
    this.allDocumentsSelected = this.galleryDocuments().length === this.selectedDocuments().length;
  });

  public get batchDownloadDocumentUrl(): string {
    return `${environment.apiConfiguration.baseUrl}${
      WorkDocumentsV2Service.DocumentsV2ControllerDownloadDocumentsPath
    }?ids=${this.selectedDocuments()
      .map((document) => document.id)
      .join(',')}`;
  }

  ngOnInit() {
    this.addWindowFilesDragListeners();
  }

  ngOnDestroy() {
    this.endSelectionMode();
    this.removeWindowFilesListeners();
  }

  public toggleDocumentSelection(document: WorkDocument) {
    if (this.selectedDocuments().includes(document)) {
      this.selectedDocuments.update((currentDocuments) => currentDocuments.filter((d) => d.id !== document.id));
    } else {
      this.selectedDocuments.update((currentDocuments) => [...currentDocuments, document]);
      this.lastSelectedDocument = document;
    }
    this.checkIfAllDocumentsAreSelectedForDay(document.upload);
    this.checkSelectionMode();
  }

  public toggleDaySelection(documents: WorkDocument[], selectAll: boolean) {
    documents.forEach((document) => {
      const isSelected = this.selectedDocuments().includes(document);
      if ((selectAll && !isSelected) || (!selectAll && isSelected)) {
        this.toggleDocumentSelection(document);
      }
    });
  }

  public batchToggleDocumentSelection(document: WorkDocument) {
    const isSelected = this.selectedDocuments().some((selectedDocument) => selectedDocument.id === document.id);
    this.batchSelectionDocuments.forEach((batchDocument) => {
      const batchDocumentIsSelected = this.selectedDocuments().includes(batchDocument);
      const shouldToggle = (isSelected && batchDocumentIsSelected) || (!isSelected && !batchDocumentIsSelected);

      if (shouldToggle) {
        this.toggleDocumentSelection(batchDocument);
      }
    });
    this.lastSelectedDocument = document;
  }

  public determineImageAction(document: WorkDocument) {
    if (this.batchSelectionActive) {
      this.batchToggleDocumentSelection(document);
    } else if (this.selectionModeActive) {
      this.toggleDocumentSelection(document);
    } else {
      this.openLightBox(document);
    }
  }

  public onCheckBoxClick($event: MouseEvent, document: WorkDocument) {
    // Prevent the checkbox click from propagating to the parent
    $event.stopPropagation();
    if (this.batchSelectionActive) {
      this.batchToggleDocumentSelection(document);
    } else {
      this.toggleDocumentSelection(document);
    }
  }

  public openLightBox(document: WorkDocument) {
    this.lightbox.open(document);
  }

  public selectAllToggle() {
    if (this.allDocumentsSelected) {
      this.endSelectionMode();
    } else {
      this.galleryDocuments().forEach((document) => {
        if (!this.selectedDocuments().includes(document)) {
          this.toggleDocumentSelection(document);
        }
      });
    }
    this.allDocumentsSelected = !this.allDocumentsSelected;
  }

  public endSelectionMode() {
    this.selectionModeActive = false;
    this.selectedDocuments.set([]);
    this.documentsByDate.update((days) => {
      days.forEach((day) => (day.selected = false));
      return days;
    });
    this.removeKeyboardListeners();
  }

  public pauseSelectionMode() {
    this.selectionModeActive = false;
    this.removeKeyboardListeners();
  }

  public continueSelectionMode() {
    // use setTimeout to avoid interference with other keyboard listeners (e.g. modal esc)
    setTimeout(() => {
      this.selectionModeActive = true;
      this.addKeyboardListeners();
    });
  }

  public async batchDeleteSelectedDocuments() {
    try {
      await lastValueFrom(
        this.documentsV2Service.documentsV2ControllerBatchDelete({
          ids: this.selectedDocuments().map((document) => document.id),
        }),
      );
      this.documentDeleted.emit(this.selectedDocuments());
      this.alertDialogService.showAlert({
        title: 'LOESCHEN_ERFOLG',
        feedbackState: 'success',
      });
    } catch (documentBatchDeleteException) {
      this.exceptionHandlingService.handleExceptionWithAlert('documentBatchDeleteException', documentBatchDeleteException);
    }
  }

  public async uploadFiles(files: File[]) {
    this.fileUploadLoadingState.set('loading');
    this.uploadingStateChanged.emit(true);
    try {
      const documents: WorkDocument[] = [];
      for (const file of files) {
        documents.push(
          await lastValueFrom(
            this.documentsV2Service.documentsV2ControllerUploadFile({
              body: {
                file: file,
                documentsubtypeId: this.documentsubtypeId(),
                documenttypeId: this.documentTypeId(),
                documentTypeIdentifierName: this.documentTypeIdentifierName(),
                folder: this.folder(),
                projectId: this.projectId(),
                prospectId: this.prospectId(),
              },
            }),
          ),
        );
      }
      this.fileUploadLoadingState.set('success');
      this.uploadingStateChanged.emit(false);
      this.alertDialogService.showAlert({
        title: 'UPLOAD_ERFOLG',
        feedbackState: 'success',
      });
      this.newDocumentsUploaded.emit(documents);
    } catch (documentUploadException) {
      this.fileUploadLoadingState.set('error');
      this.uploadingStateChanged.emit(false);
      this.exceptionHandlingService.handleExceptionWithAlert('documentUploadException', documentUploadException, {
        title: 'UPLOAD_FEHLER',
        message: 'BITTE_ERNEUT_VERSUCHEN',
        feedbackState: 'error',
      });
    }
  }

  public setBatchSelection(hoveredDocument?: WorkDocument) {
    if (hoveredDocument) {
      this.batchSelectionHoveredDocument = hoveredDocument;
    }
    if (!this.batchSelectionActive || !this.lastSelectedDocument || !this.batchSelectionHoveredDocument) {
      return;
    }
    // Flatten documentsByDate to a single array of all WorkDocuments
    const allDocuments: WorkDocument[] = this.documentsByDate().flatMap((day) => day.documents);

    // Find the indices of lastSelectedDocument and hoveredDocument
    const startIndex = allDocuments.indexOf(this.lastSelectedDocument);
    const endIndex = allDocuments.indexOf(this.batchSelectionHoveredDocument);
    if (startIndex === -1 || endIndex === -1) {
      console.warn('Documents not found in the current gallery.');
      return;
    }

    // Determine the range (start to end)
    const [start, end] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];

    // Get the range of documents and update batchSelectionDocuments
    this.batchSelectionDocuments = allDocuments.slice(start, end + 1);
  }

  private endBatchSelection() {
    this.batchSelectionActive = false;
    this.batchSelectionDocuments = [];
  }

  private orderDocumentsByDate() {
    const documents = this.galleryDocuments();

    if (!documents || documents.length === 0) {
      this.documentsByDate.set([]);
      return;
    }

    const groupedByDate: Record<string, WorkDocument[]> = documents.reduce((acc, document) => {
      if (!document.upload) {
        console.warn('Document with missing date found:', document);
        return acc;
      }

      const date = dayjs(document.upload).format('YYYY-MM-DD');
      acc[date] = acc[date] || [];
      acc[date].push(document);
      return acc;
    }, {} as Record<string, WorkDocument[]>);

    this.documentsByDate.set(
      Object.entries(groupedByDate)
        .map(([date, documents]) => ({ date, selected: false, documents }))
        .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()),
    );
  }

  private checkIfAllDocumentsAreSelectedForDay(date: string | null) {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    this.documentsByDate.update((currentDays) => {
      return currentDays.map((day) => {
        if (day.date === formattedDate) {
          return {
            ...day,
            selected: day.documents.every(
              (document) => this.selectedDocuments().find((selectedDocument) => selectedDocument.id === document.id) !== undefined,
            ),
          };
        }
        return day;
      });
    });
  }

  private checkSelectionMode() {
    const previousSelectionState = this.selectionModeActive;
    this.selectionModeActive = this.selectedDocuments().length > 0;
    if (previousSelectionState !== this.selectionModeActive) {
      if (this.selectionModeActive) {
        this.addKeyboardListeners();
      } else {
        this.endBatchSelection();
        this.removeKeyboardListeners();
      }
    }
  }

  private handleKeyboardEvents(event: KeyboardEvent) {
    if (event.type === 'keydown') {
      switch (event.key) {
        case 'Escape': {
          this.endSelectionMode();
          break;
        }
        case 'Shift': {
          this.batchSelectionActive = true;
          this.setBatchSelection();
          break;
        }
      }
    } else if (event.type === 'keyup' && event.key === 'Shift') {
      this.endBatchSelection();
    }
  }

  private addKeyboardListeners() {
    window.addEventListener('keydown', this.boundHandleKeyboardEvents);
    window.addEventListener('keyup', this.boundHandleKeyboardEvents);
  }

  private removeKeyboardListeners() {
    window.removeEventListener('keydown', this.boundHandleKeyboardEvents);
    window.removeEventListener('keyup', this.boundHandleKeyboardEvents);
  }

  private addWindowFilesDragListeners() {
    window.addEventListener('dragenter', this.onDragEnter, false);
  }

  private removeWindowFilesListeners() {
    window.removeEventListener('dragenter', this.onDragEnter, false);
  }

  private onDragEnter = (event: DragEvent) => {
    if (this.isFileDragEvent(event)) {
      this.showUploadOnDesktop.set(true);
    }
  };

  private isFileDragEvent(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }
}
